import React, { createContext, useContext, useEffect, useState } from "react";
import { Toaster } from "sonner";
import { AppSidebar } from "./components/AppSidebar";
import { PedidosView } from "./components/PedidosView";
import { PruebasView } from "./components/PruebasView";
import {
  SidebarProvider,
  SidebarTrigger,
} from "./components/ui/sidebar";
import { UsuariosView } from "./components/UsuariosView";
import { ClientesView } from "./components/ClientesView";
import { PermisosView } from "./components/PermisosView";
import { TicketsView } from "./components/tickets/TicketsView";
import { DocumentacionView } from "./components/DocumentacionView";
import { supabase } from "./lib/supabase";
import { obtenerPermisos } from "./services/pruebasService";
import { LoginView } from "./Login";
import { PRUEBA_NOMBRE_MAP } from "./constants/pruebasMappings";
import { ROL_LABELS } from "./lib/permisos";

export const UserRolContext = createContext<string>("");
export const useUserRol = () => useContext(UserRolContext);

export const UserNameContext = createContext<string>("");
export const useUserName = () => useContext(UserNameContext);

const PermisosMapContext = createContext<Record<string, Record<string, boolean>>>({});

export function useTienePermiso(accion: string): boolean {
  const rol = useUserRol();
  const permisosPorRol = useContext(PermisosMapContext);
  if (rol === 'ADMIN') return true;
  return !!permisosPorRol[rol]?.[accion];
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(_error: any, _errorInfo: any) { }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen w-full">
          <div className="w-full max-w-md">
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-center">
              Ocurrió un error inesperado en la interfaz.<br />
              {this.state.error?.message || String(this.state.error)}
            </div>
            <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => window.location.reload()}>
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

type MainLayoutProps = {
  user: { name: string; email: string; avatar: string };
  usuarios: any[];
  clientes: any[];
  pedidos: any[];
  permisos: { rol_id: string; accion: string; permitido: boolean }[];
  activeSection: string;
  setActiveSection: (section: string) => void;
  onLogout: () => void;
  userName: string;
  pruebasGuardadas?: { [pedidoId: string]: { [prueba: string]: { row_id: string } } };
  onRefreshBackend?: () => void;
};

function MainLayout({
  user, usuarios, clientes, pedidos, permisos, activeSection, setActiveSection,
  onLogout, userName, pruebasGuardadas = {}, onRefreshBackend,
}: MainLayoutProps) {
  const email = (typeof window !== 'undefined' && localStorage.getItem('userEmail')) || (user?.email || '');
  const userRol = React.useMemo(() => {
    if (!email || !usuarios.length) return '';
    const usuario = usuarios.find(u => (u.email === email || u.CORREO === email));
    return usuario?.puesto || '';
  }, [email, usuarios]);

  const permisosPorRol = React.useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {};
    permisos.forEach(p => {
      if (!map[p.rol_id]) map[p.rol_id] = {};
      map[p.rol_id][p.accion] = p.permitido;
    });
    return map;
  }, [permisos]);

  const puedeVerContactos = userRol === 'ADMIN' || !!permisosPorRol[userRol]?.['ver_contactos'];

  const puedeAutorizarReapertura = React.useMemo(() => {
    if (!email || !usuarios.length) return false;
    const usuario = usuarios.find(u => (u.email === email || u.CORREO === email));
    return userRol === 'ADMIN' || usuario?.autoriza_reapertura === true;
  }, [email, usuarios, userRol]);

  const isPedidosActive = activeSection === 'pedidos' || !['pruebas', 'clientes', 'usuarios', 'permisos', 'documentacion', 'tickets'].includes(activeSection);

  return (
    <UserRolContext.Provider value={userRol}>
      <PermisosMapContext.Provider value={permisosPorRol}>
        <SidebarProvider>
          <div className="flex min-h-screen w-full">
            <AppSidebar activeSection={activeSection} onSectionChange={setActiveSection} user={user} onLogout={onLogout} />
            <main className="flex-1 p-6">
              <div className="mb-4"><SidebarTrigger /></div>
              {/* PedidosView stays mounted always to preserve form state across section switches */}
              <div style={{ display: isPedidosActive ? 'block' : 'none' }}>
                <PedidosView pedidos={pedidos} userName={userName} pruebasGuardadas={pruebasGuardadas} userRol={userRol} onRefreshBackend={onRefreshBackend} puedeAutorizarReapertura={puedeAutorizarReapertura} />
              </div>
              {activeSection === 'pruebas' && <PruebasView user={user} />}
              {activeSection === 'clientes' && puedeVerContactos && <ClientesView clientes={clientes} pedidos={pedidos} onRefresh={onRefreshBackend} />}
              {activeSection === 'usuarios' && userRol === 'ADMIN' && <UsuariosView usuarios={usuarios} onRefresh={onRefreshBackend} />}
              {activeSection === 'permisos' && userRol === 'ADMIN' && <PermisosView />}
              {activeSection === 'tickets' && <TicketsView />}
              {activeSection === 'documentacion' && <DocumentacionView />}
            </main>
          </div>
        </SidebarProvider>
      </PermisosMapContext.Provider>
    </UserRolContext.Provider>
  );
}

export default function App() {
  const [activeSection, setActiveSection] = useState("pedidos");
  const [user, setUser] = useState<{ name: string; email: string; avatar: string } | null>(null);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [permisos, setPermisos] = useState<{ rol_id: string; accion: string; permitido: boolean }[]>([]);
  const [pruebasGuardadasMap, setPruebasGuardadasMap] = useState<{
    [pedidoId: string]: { [prueba: string]: { row_id: string } }
  }>({});
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [backendError, setBackendError] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);

  // ─── SUPABASE AUTH ──────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const u = session.user;
        setUser({
          name: u.user_metadata?.full_name || u.email || "",
          email: u.email || "",
          avatar: u.user_metadata?.avatar_url || "",
        });
        localStorage.setItem('userEmail', u.email || "");
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = session.user;
        setUser({
          name: u.user_metadata?.full_name || u.email || "",
          email: u.email || "",
          avatar: u.user_metadata?.avatar_url || "",
        });
        localStorage.setItem('userEmail', u.email || "");
        setLoginError("");
      } else {
        setUser(null);
        setUsuarios([]);
        setPedidos([]);
        localStorage.removeItem('userEmail');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ─── FETCH DATOS DESDE SUPABASE ─────────────────────────────────────────────
  async function fetchBackendData(_user: { name: string; email: string; avatar: string }, { silent = false } = {}) {
    if (!silent) setLoading(true);
    setBackendError("");
    try {
      // 1. Usuarios
      const { data: usuariosData, error: usuariosError } = await supabase
        .from('usuarios')
        .select('*');
      if (usuariosError) throw usuariosError;

      const usuariosMapped = (usuariosData || []).map((u: any) => ({
        id: u.id,
        nombre: u.nombre || "?",
        email: (u.correo || "").trim().toLowerCase(),
        rol: ROL_LABELS[u.puesto] || u.puesto || "Analista",
        estado: "Activo",
        ultimaConexion: u.created_at || new Date().toISOString(),
        puesto: u.puesto || '',
        area: u.area || '',
        autoriza_reapertura: u.autoriza_reapertura === true,
      }));
      setUsuarios(usuariosMapped);

      // 1b. Permisos por rol (matriz editable desde la pantalla de Permisos)
      const permisosData = await obtenerPermisos();
      setPermisos(permisosData);

      // 2. Clientes
      const { data: clientesData, error: clientesError } = await supabase
        .from('clientes')
        .select('*')
        .order('nombre', { ascending: true });
      if (clientesError) throw clientesError;
      setClientes(clientesData || []);

      // 3. Pedidos con sus noms
      const { data: pedidosData, error: pedidosError } = await supabase
        .from('pedidos')
        .select(`
          id, folio, origen_numero, articulo, nombre_cliente, laboratorio,
          created_at, fecha_emision, fecha_entrega, cerrar_pedido, bitacora_link, cliente_link,
          cantidad, muestra, descripcion, telefono, correo, direccion, cliente_id,
          noms (
            id,
            tipo_prueba,
            row_id_prueba,
            aceptado,
            doc,
            razon_rechazo,
            editado,
            editado_por,
            estado,
            mostrar_observaciones,
            incluir_reporte_cliente,
            orden
          )
        `)
        .order('created_at', { ascending: false });
      if (pedidosError) throw pedidosError;

      const guardadasMap: { [pedidoId: string]: { [prueba: string]: { row_id: string } } } = {};

      // Compute company-sequential folio per (nombre_cliente, tipo_prueba)
      // Collect all noms across all pedidos, sort by row_id_prueba (creation order), assign 1,2,3...
      const allNomsFlat: { nomId: string; tipoPrueba: string; rowId: number; nombreCliente: string }[] = [];
      (pedidosData || []).forEach((p: any) => {
        const cliente = (p.nombre_cliente || 'sin_cliente').trim();
        (p.noms || []).forEach((n: any) => {
          allNomsFlat.push({ nomId: String(n.id), tipoPrueba: n.tipo_prueba, rowId: Number(n.row_id_prueba) || 0, nombreCliente: cliente });
        });
      });
      allNomsFlat.sort((a, b) => a.rowId - b.rowId);
      const nomFolioMap: Record<string, number> = {};
      const folioCounters: Record<string, number> = {};
      allNomsFlat.forEach(nom => {
        const key = `${nom.nombreCliente}__${nom.tipoPrueba}`;
        folioCounters[key] = (folioCounters[key] || 0) + 1;
        nomFolioMap[nom.nomId] = folioCounters[key];
      });

      const pedidosMapped = (pedidosData || []).map((p: any) => {
        // Orden manual (drag and drop): pruebas sin `orden` asignado (NULL) van al final,
        // en su orden de creación (row_id_prueba), para no desordenar nada existente.
        const nomsOrdenados = [...(p.noms || [])].sort((a: any, b: any) => {
          const ordenA = a.orden ?? Infinity;
          const ordenB = b.orden ?? Infinity;
          if (ordenA !== ordenB) return ordenA - ordenB;
          return (Number(a.row_id_prueba) || 0) - (Number(b.row_id_prueba) || 0);
        });
        const pruebasSolicitadas = nomsOrdenados.map((n: any) => ({
          nombre: PRUEBA_NOMBRE_MAP[n.tipo_prueba] || n.tipo_prueba,
          ROW_ID: n.row_id_prueba ? String(n.row_id_prueba) : '',
          nomId: String(n.id),
          ACEPTADO: n.aceptado || '',
          DOC: n.doc || '',
          RAZON_RECHAZO: n.razon_rechazo || '',
          EDITADO: n.editado || false,
          EDITADO_POR: n.editado_por || '',
          folioEmpresa: nomFolioMap[String(n.id)] || undefined,
          EN_BORRADOR: n.estado === 'borrador',
          MOSTRAR_OBSERVACIONES: n.mostrar_observaciones === true,
          INCLUIR_REPORTE_CLIENTE: n.incluir_reporte_cliente !== false,
        }));

        // Mapa de pruebas guardadas para este pedido — keyed by nomId__pruebaNombre to support duplicates.
        // Solo se incluyen pruebas con row_id_prueba real: sin eso no hay datos capturados
        // todavía, y usar n.id (el UUID de noms) como fallback rompía el botón REVISAR
        // (parseInt sobre un UUID da NaN → "invalid input syntax for type integer: NaN").
        guardadasMap[p.id] = {};
        (p.noms || []).forEach((n: any) => {
          if (!n.row_id_prueba) return;
          const pruebaNombre = PRUEBA_NOMBRE_MAP[n.tipo_prueba] || n.tipo_prueba;
          const key = `${n.id}__${pruebaNombre}`;
          guardadasMap[p.id][key] = {
            row_id: n.row_id_prueba,
            ACEPTADO: n.aceptado || '',
            DOC: n.doc || '',
          };
        });

        return {
          id: p.id,
          numeroPedido: p.origen_numero || String(p.folio),
          tipoPrueba: (p.laboratorio || '').toUpperCase().includes("QUIMICO") ? "Quimico" : "Fisico",
          fechaEmision: p.fecha_emision || p.created_at,
          fechaEntrega: p.fecha_entrega,
          createdAt: p.created_at,
          articulos: [p.articulo].filter(Boolean),
          pruebasSolicitadas,
          CERRAR_PRUEBA: p.cerrar_pedido === true || p.cerrar_pedido === 'true',
          BITACORA_LINK: p.bitacora_link || '',
          CLIENTE_LINK: p.cliente_link || '',
          CANTIDAD: p.cantidad,
          clienteId: p.cliente_id || undefined,
          nombre_cliente: p.nombre_cliente,
          muestra: p.muestra,
          descripcion: p.descripcion,
          telefono: p.telefono,
          correo: p.correo,
          direccion: p.direccion,
        };
      });

      setPedidos(pedidosMapped);
      setPruebasGuardadasMap(guardadasMap);
      setBackendError("");
      setDataLoaded(true);
    } catch (err: any) {
      setBackendError("No se pudo conectar con Supabase. " + (err.message || ''));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user && !dataLoaded) {
      fetchBackendData(user);
    }
  }, [user, dataLoaded]);

  // Reset dataLoaded cuando se cierra sesión
  useEffect(() => {
    if (!user) setDataLoaded(false);
  }, [user]);

  // Valida que el email esté autorizado
  useEffect(() => {
    if (user && usuarios.length) {
      const emailAuth = (user.email || '').trim().toLowerCase();
      const autorizado = usuarios.some(u => (u.email || '').trim().toLowerCase() === emailAuth);
      if (!autorizado) {
        supabase.auth.signOut();
        setUser(null);
        setUsuarios([]);
        setPedidos([]);
        setLoginError('Tu email no está autorizado para acceder.');
        localStorage.removeItem('userEmail');
      }
    }
  }, [user, usuarios]);

  const userName = React.useMemo(() => {
    if (!(user && usuarios.length)) return '';
    const email = ((typeof window !== 'undefined' && localStorage.getItem('userEmail')) || user?.email || '').trim().toLowerCase();
    const usuarioMatch = usuarios.find(u => (u.email || '').trim().toLowerCase() === email);
    return usuarioMatch?.nombre || '';
  }, [user, usuarios]);

  useEffect(() => {
    if (userName) localStorage.setItem('userName', userName);
  }, [userName]);

  // ─── RENDERS ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <div className="text-lg text-muted-foreground animate-pulse">Cargando...</div>
      </div>
    );
  }

  if (user && !usuarios.length) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <div className="text-lg text-muted-foreground animate-pulse">Verificando acceso...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <div className="w-full max-w-md">
          {loginError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-center">{loginError}</div>
          )}
          <LoginView onLogin={() => { }} />
        </div>
      </div>
    );
  }

  if (backendError) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <div className="w-full max-w-md">
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-center">{backendError}</div>
          <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => fetchBackendData(user)}>
            Reintentar conexión
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Toaster position="top-right" richColors />
      <UserNameContext.Provider value={userName}>
        <MainLayout
          user={user}
          usuarios={usuarios}
          clientes={clientes}
          pedidos={pedidos}
          permisos={permisos}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onLogout={async () => {
            await supabase.auth.signOut();
            setUser(null);
            setUsuarios([]);
            setPedidos([]);
            setLoginError("");
            localStorage.removeItem('userEmail');
          }}
          userName={userName}
          pruebasGuardadas={pruebasGuardadasMap}
          onRefreshBackend={user ? () => fetchBackendData(user, { silent: true }) : undefined}
        />
      </UserNameContext.Provider>
    </ErrorBoundary>
  );
}